import { Component, OnInit } from '@angular/core';
import { MemberRole, NoAdminMode, WaitingType } from '@app/core/interfaces/c5/enumerations';
import { MeetingMember } from '@app/core/interfaces/c5/meeting-member';
import { Meeting } from '@app/core/interfaces/portal/meeting';
import { MeetingUpdate } from '@app/core/interfaces/portal/meeting-update';
import { Site } from '@app/core/interfaces/site';
import { DialogComponent } from '@app/shared/dialog/dialog-component.directive';
import { EnhancedEndUserLite } from '@app/shared/models/enhanced-end-user-lite';
import { TypeaheadEntry } from '@app/shared/services/typeahead-basket.service';
import { toMap } from '@app/utils/common';
import { PortalError } from '@app/utils/error/portal-error';
import { RequiredValidator } from '@app/utils/form-mapping/helpers/default-validators';
import { MeetingService } from '@business/meeting/meeting.service';
import { MeetingForm } from '@business/meeting/models/meeting-form';
import { MeetingViewModel } from '@business/meeting/models/meeting-view-model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { FormGroupTyped } from '../../../../../types/typed-forms';

interface UpdateMeetingInput {
  defaultSiteId: number;
  endUsers: EnhancedEndUserLite[];
  meeting?: MeetingViewModel;
  siteId?: number;
  sites: Site[];
}

@UntilDestroy()
@Component({
  selector: 'portal-update-meeting',
  templateUrl: './update-meeting.component.html',
  styles: [],
  standalone: false,
})
export class UpdateMeetingComponent
  extends DialogComponent<UpdateMeetingInput, void>
  implements OnInit, UpdateMeetingInput
{
  defaultSiteId: number;
  endUsers: EnhancedEndUserLite[];
  meeting?: MeetingViewModel;
  siteId?: number;
  sites: Site[];

  availableNewMembers!: EnhancedEndUserLite[];
  error: PortalError | null = null;
  form!: FormGroupTyped<MeetingForm>;
  members!: Record<number, MeetingMember>;
  roles!: TypeaheadEntry[];
  saving: boolean = false;
  userMap!: Record<number, EnhancedEndUserLite>;

  constructor(
    private meetingService: MeetingService,
    ngbActiveModal: NgbActiveModal
  ) {
    super(ngbActiveModal);
  }

  ngOnInit(): void {
    this.form = MeetingForm.formGroup();
    this.roles = Object.values(MemberRole).map((role) => {
      return {
        label: role,
        value: role,
      };
    });
    this.userMap = toMap(this.endUsers, 'id');

    if (this.meeting != null) {
      this.form.reset({
        ...MeetingForm.from({
          ...this.meeting._original.data,
          members: this.meeting.members,
        }),
      });

      this.members = toMap(this.meeting.members, 'userId');
    } else {
      this.form.reset({
        adminTimeout: 0,
        dialIn: true,
        members: [],
        noAdminMode: NoAdminMode.TALK,
        noAdminTimeout: 0,
        name: '',
        recordName: false,
        scheduledMeeting: true,
        videoMode: true,
        waitingType: WaitingType.SILENCE, // only SILENCE supported currently
      });

      this.availableNewMembers = this.endUsers;
      this.members = {};
    }

    if (this.form.get('siteId').value == null) {
      if (this.siteId != null) {
        this.form.get('siteId').setValue(this.siteId);
      } else if (this.sites.length === 1) {
        this.form.get('siteId').setValue(this.sites[0].id!);
      } else if (this.sites.length > 1) {
        this.form.get('siteId').setValue(this.defaultSiteId);
      }
    }

    this.form
      .get('scheduledMeeting')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((scheduled) => {
        this.handleScheduledMeetingChange(scheduled);
      });

    this.handleScheduledMeetingChange(this.form.value.scheduledMeeting);
  }

  save(): void {
    const model: MeetingUpdate = MeetingForm.merge({}, this.form.value) as any;

    this.saving = true;

    let update$: Observable<Meeting>;

    if (this.meeting != null) {
      update$ = this.meetingService.update(this.meeting.id!, model);
    } else {
      update$ = this.meetingService.create(model);
    }

    update$
      .pipe(
        finalize(() => {
          this.saving = false;
        })
      )
      .subscribe({
        error: (error) => {
          this.error = error;
        },
        next: () => {
          this.close();
        },
      });
  }

  private handleScheduledMeetingChange(scheduled: boolean): void {
    if (scheduled) {
      this.form.get('duration').addValidators(RequiredValidator);
      this.form.get('start').addValidators(RequiredValidator);
    } else {
      this.form.get('duration').removeValidators(RequiredValidator);

      if (this.form.get('start').value == null) {
        this.form.get('start').setValue(new Date());
      }
    }

    this.form.get('duration').updateValueAndValidity();
    this.form.get('start').updateValueAndValidity();
  }
}
